import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  FolderOpen,
  Users,
  Clock,
  DollarSign,
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import { fetchPaymentRequests } from '../../services/PaymentService';

const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

interface Project {
  _id: string;
  title: string;
  status: string;
  progress: number;
  deadline: string;
  budget: {
    amount: number;
    currency: string;
  };
  team: any[];
}

interface DashboardStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  totalBudget: number;
  totalSpent: number;
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};


const ClientDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalBudget: 0,
    totalSpent: 0,
  });
  const [chartData, setChartData] = useState<{ bar: any[]; pie: any[] }>({
    bar: [],
    pie: [],
  });

  const [paymentStats, setPaymentStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
  });

  const [loading, setLoading] = useState(true);

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const payments = await fetchPaymentRequests();
        const stats = {
          total: payments.length,
          paid: payments.filter((p) => p.status === 'paid').length,
          pending: payments.filter((p) => p.status === 'pending').length,
          overdue: payments.filter((p) => p.status === 'overdue').length,
        };
        setPaymentStats(stats);
      } catch (err) {
      }
    };
    loadPayments();
  }, []);

  // Redirect based on user role
  useEffect(() => {
    if (user && user.role !== 'client') {
      if (user.role === 'developer') {
        navigate('/dashboard/developer', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/dashboard/admin', { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === 'client') {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('accessToken');

      // Fetch projects
      const projectsResponse = await fetch(`${API_BASE_URL}/projects?limit=5`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setProjects(projectsData.projects || []);

        // Calculate stats
        const totalProjects = projectsData.projects?.length || 0;
        const activeProjects =
          projectsData.projects?.filter((p: Project) => p.status === 'active')
            .length || 0;
        const completedProjects =
          projectsData.projects?.filter(
            (p: Project) => p.status === 'completed'
          ).length || 0;
        const totalBudget =
          projectsData.projects?.reduce(
            (sum: number, p: Project) => sum + p.budget.amount,
            0
          ) || 0;

        setStats({
          totalProjects,
          activeProjects,
          completedProjects,
          totalBudget,
          totalSpent: totalBudget * 0.6, // Mock data for spent amount
        });
        // Chart data preparation
        setChartData({
          bar: [
            { name: 'Total', value: totalProjects },
            { name: 'Active', value: activeProjects },
            { name: 'Completed', value: completedProjects },
          ],
          pie: [
            { name: 'Active', value: activeProjects },
            { name: 'Completed', value: completedProjects },
            {
              name: 'Pending',
              value: totalProjects - activeProjects - completedProjects,
            },
          ],
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'on-hold':
        return 'text-gray-600 bg-gray-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getTimeUntilDeadline = (deadline: string) => {
    const days = Math.ceil(
      (new Date(deadline).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    return `${days} days left`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded-lg w-64 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded-lg w-32 animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-lg shadow-sm border animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here's what's happening with your projects today.
          </p>
        </div>
        <Link
          to="/dashboard/projects/create"
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Projects
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalProjects}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">+12% from last month</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Active Projects
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.activeProjects}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-gray-600">
              {stats.completedProjects} completed
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Budget</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.totalBudget)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600">
              {formatCurrency(stats.totalSpent)} spent
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Team Members</p>
              <p className="text-2xl font-bold text-gray-900">
                {projects.reduce(
                  (sum, project) => sum + project.team.length,
                  0
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600">Across all projects</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Total Payments</h4>
          <p className="text-xl font-bold">{paymentStats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Paid</h4>
          <p className="text-xl font-bold text-green-600">
            {paymentStats.paid}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Pending</h4>
          <p className="text-xl font-bold text-yellow-500">
            {paymentStats.pending}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Overdue</h4>
          <p className="text-xl font-bold text-red-500">
            {paymentStats.overdue}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <a
          href="/dashboard/payments"
          className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          View All Payments
        </a>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            Project Overview
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.bar}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            Project Status
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.pie}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label
              >
                {chartData.pie.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={pieColors[index % pieColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Projects
            </h2>
            <Link
              to="/dashboard/projects"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View all
            </Link>
          </div>
        </div>

        <div className="p-6">
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No projects yet
              </h3>
              <p className="text-gray-600 mb-6">
                Get started by creating your first project.
              </p>
              <Link
                to="/dashboard/projects/create"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {projects.map((project) => (
                <div
                  key={project._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`/dashboard/projects/${project._id}`}
                        className="text-lg font-medium text-gray-900 hover:text-blue-600 truncate"
                      >
                        {project.title}
                      </Link>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                          project.status
                        )}`}
                      >
                        {project.status}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center space-x-6 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {getTimeUntilDeadline(project.deadline)}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {project.team.length} team member
                        {project.team.length !== 1 ? 's' : ''}
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {formatCurrency(
                          project.budget.amount,
                          project.budget.currency
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="ml-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {project.progress}% Complete
                      </div>
                      <div className="mt-1 w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/dashboard/projects/create"
          className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
        >
          <Plus className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Start New Project</h3>
          <p className="text-blue-100">
            Create a new project and assemble your team
          </p>
        </Link>

        <Link
          to="/dashboard/payments"
          className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
        >
          <DollarSign className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Payment Center</h3>
          <p className="text-green-100">
            Manage payments and milestone releases
          </p>
        </Link>

        <Link
          to="/dashboard/chat"
          className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white hover:from-purple-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
        >
          <Users className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Team Chat</h3>
          <p className="text-purple-100">
            Communicate with your development team
          </p>
        </Link>
      </div>
    </div>
  );
};

export default ClientDashboard;
